import { css } from '@emotion/react'
import { StudentLecture, type StudentLectureDtoT } from '@mathflat/domain/@entities/StudentLecture'
import { s3URL } from '@mathflat/shared/@common/utils/s3'
import clsx from 'clsx'
import { differenceInCalendarDays, startOfDay } from 'date-fns'
import { format } from 'date-fns'
import { useCallback, useEffect, useRef } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'

import { tJosa } from '~/@app/i18n'
import { routeName } from '~/@common/constants'
import modalService from '~/@common/services/modal.service'
import { typo } from '~/@common/styles'
import Modal from '~/@common/ui/modal/Modal'

const formatDate = (dateString: string) => format(new Date(dateString), 'yy.MM.dd')
const getDDayLabel = (dueAt: string): string | null => {
  const diff = differenceInCalendarDays(startOfDay(new Date(dueAt)), startOfDay(new Date()))
  if (diff === 0) return 'D-0'
  if (diff === 1) return 'D-1'
  return null
}

type Props = {
  assignments: StudentLectureDtoT.AssignmentResponse[]
  onLoadMore?: () => void
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  isFetched?: boolean
  isFiltered?: boolean
}

const MobileLectureCardList = ({
  assignments,
  onLoadMore,
  hasNextPage,
  isFetchingNextPage,
  isFetched = false,
  isFiltered = false,
}: Props) => {
  const navigate = useNavigate()
  const sentinelRef = useRef<HTMLDivElement>(null)

  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (!entries[0]) return
      if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
        onLoadMore?.()
      }
    },
    [hasNextPage, isFetchingNextPage, onLoadMore],
  )

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const observer = new IntersectionObserver(handleIntersect, { threshold: 0 })
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [handleIntersect])

  const handleClickLecture = (assignment: StudentLectureDtoT.AssignmentResponse) => {
    if (assignment.status === 'EXPIRED' || assignment.status === 'IN_PROGRESS_EXPIRED') {
      modalService.openModal(
        <Modal.Alert
          confirm={{
            onClick: modalService.closeAllModal,
          }}
        >
          시청기한이 만료된 {tJosa('강의')}입니다.
        </Modal.Alert>,
        { modalName: 'expired-lecture-notice' },
      )
      return
    }

    navigate(
      generatePath(routeName.student.assignedLectureDetail, {
        lectureId: String(assignment.lectureId),
        assignmentId: String(assignment.assignmentId),
      }),
    )
  }

  if (isFetched && assignments.length === 0) {
    return (
      <div css={S.emptyContainer}>
        <img
          css={S.emptyImage}
          src={s3URL.student('images/airplain_premium.svg')}
          alt={`${tJosa('출제')}된 ${tJosa('강의')} 없음`}
        />
        <p css={S.emptyText}>
          {isFiltered
            ? '조건에 맞는 결과가 없습니다.'
            : `선생님이 ${tJosa('출제')}한 ${tJosa('강의', '이/가')} 없습니다.`}
        </p>
      </div>
    )
  }

  return (
    <div css={S.cardList}>
      {assignments.map((assignment) => (
        <button
          type="button"
          key={assignment.assignmentId}
          css={S.card}
          onClick={() => handleClickLecture(assignment)}
        >
          <div css={S.cardThumbnail}>
            {assignment.dueAt && getDDayLabel(assignment.dueAt) && (
              <span css={S.dDayBadge}>{getDDayLabel(assignment.dueAt)}</span>
            )}
            {assignment.thumbnailUrl && (
              <img
                src={assignment.thumbnailUrl}
                alt={`${tJosa('강의')} 썸네일`}
                loading="lazy"
                decoding="async"
              />
            )}
          </div>
          <div css={S.cardContent}>
            <p css={S.cardTitle}>{assignment.lectureTitle}</p>
            <div css={S.cardInfo}>
              <span css={S.cardInfoText}>
                <span css={S.cardInfoValue}>{formatDate(assignment.assignedAt)}</span>
                <span css={S.cardInfoLabel}> {tJosa('출제')}</span>
              </span>

              {assignment.dueAt && (
                <>
                  <span css={S.cardInfoDivider} />
                  <span css={S.cardInfoText}>
                    <span css={S.cardInfoValue}>
                      {formatDate(assignment.assignedAt)} - {formatDate(assignment.dueAt)}
                    </span>
                    <span css={S.cardInfoLabel}>까지 시청</span>
                  </span>
                </>
              )}

              <span css={S.cardInfoDivider} />
              <span css={S.cardStatus} className={clsx(assignment.status)}>
                {StudentLecture.ASSIGNMENT_STATUS_TEXT[assignment.status]}
              </span>
            </div>
          </div>
        </button>
      ))}
      <div ref={sentinelRef} css={S.sentinel} />
    </div>
  )
}

export default MobileLectureCardList

const S = {
  emptyContainer: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
  `,
  emptyImage: css`
    width: 100px;
    height: 100px;
    object-fit: contain;
  `,
  emptyText: css`
    ${typo.body02};
    font-weight: 600;
    color: var(--Colors-Gray-700);
  `,
  cardList: css`
    display: flex;
    flex-direction: column;
    gap: 10px;
    overflow-y: auto;
  `,
  card: css`
    display: flex;
    flex-direction: column;
    border-radius: var(--Radius-300);
    overflow: hidden;
    cursor: pointer;
    flex-shrink: 0;
  `,
  cardThumbnail: css`
    position: relative;
    border-radius: 8px 8px 0 0;
    overflow: hidden;
    background: var(--Colors-Gray-200);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  `,
  dDayBadge: css`
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 1;
    color: var(--White);
    ${typo.caption02};
    font-weight: 700;
    background-color: var(--Colors-Gray-900);
    border-radius: var(--Radius-100);
    padding: 2px 4px;
  `,
  cardContent: css`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 20px 16px;
    background: white;
  `,
  cardTitle: css`
    text-align: left;
    ${typo.body02};
    color: var(--Colors-Gray-900);
  `,
  cardInfo: css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
  `,
  cardInfoText: css`
    display: flex;
    align-items: center;
    ${typo.caption01};
  `,
  cardInfoValue: css`
    color: var(--Colors-Gray-900);
  `,
  cardInfoLabel: css`
    color: var(--Colors-Gray-600);
  `,
  cardInfoDivider: css`
    width: 1px;
    height: 8px;
    background: var(--Colors-Gray-500);
  `,
  cardStatus: css`
    ${typo.caption01};
    text-align: center;
    font-weight: 500;
    color: var(--Colors-Gray-500);

    &.NOT_STARTED {
      color: var(--Colors-Blue-500);
    }
    &.IN_PROGRESS {
      color: var(--Colors-Gray-800);
      font-weight: 400;
    }
  `,
  sentinel: css`
    height: 1px;
  `,
}
